/**
 * The /documents placeholder shown before the app shell mounts (#12073).
 *
 * It draws the page that follows it: the top bar with the page name, Add and
 * the search field; the destinations column at its remembered width; and the
 * listing with its heading,
 * the chips row and Home's own placeholders — the same ones Home draws while
 * its reads are out, so the page does not change its promise between the
 * two stages of loading. Still, like the listing's skeleton rows: nothing
 * spins.
 */
import React from "react";
import { HomePlaceholders } from "./HomeSections";
import { readStoredDocumentsNavWidth } from "./navWidth";

const bar = "block rounded-[4px] bg-[var(--fp-ink-wash)]";

/** The column's rows: an icon square and a name, at the row's height. */
const ColumnRows: React.FC<{ widths: number[]; caret?: boolean }> = ({
  widths,
  caret = false,
}) => (
  <div className="flex flex-col gap-0.5">
    {widths.map((width, index) => (
      <div key={index} className="flex h-[34px] items-center gap-2 pl-2 pr-3">
        <span className="w-8 shrink-0">
          {caret && index > 0 && <span className={`${bar} ml-auto h-2 w-2`} />}
        </span>
        <span className="h-[18px] w-[18px] shrink-0 rounded-[4px] bg-[var(--fp-ink-wash)]" />
        <span className={`${bar} h-3.5`} style={{ width }} />
      </div>
    ))}
  </div>
);

const CHIP_WIDTHS = [72, 84, 100, 128, 84, 100, 96, 108];

const DocumentsSkeletonLoader: React.FC = () => {
  const columnWidth = readStoredDocumentsNavWidth();
  return (
    <div
      className="flex h-full w-full flex-col overflow-hidden bg-[var(--fp-bg)] md:bg-[var(--fp-surface)]"
      aria-hidden
    >
      <div
        data-skeleton="topbar"
        className="flex shrink-0 items-center gap-3 border-b border-[var(--fp-border)] px-[var(--fp-page-gutter)] py-1.5"
        style={{ background: "var(--fp-topbar-ground)" }}
      >
        {/* At phone the bar leads with the menu button, the verb is its glyph
          alone and the search yields to its icon (PageTopbar). */}
        <span className="h-[30px] w-[30px] shrink-0 rounded-[7px] bg-[var(--fp-ink-wash)] md:hidden" />
        <span className={`${bar} h-4 w-[92px] shrink-0`} />
        <span className="h-[30px] w-[70px] shrink-0 rounded-[7px] bg-[var(--fp-ink-wash)] max-md:w-[30px]" />
        <span className="h-[30px] min-w-[200px] max-w-[520px] flex-1 rounded-[7px] border border-[var(--fp-border)] bg-[var(--fp-surface)] max-md:hidden" />
        <span className="ml-auto h-[30px] w-[30px] shrink-0 rounded-[7px] bg-[var(--fp-ink-wash)] md:hidden" />
        <span className="ml-auto h-[30px] w-[84px] rounded-[7px] border border-[var(--fp-border)] max-md:hidden" />
        <span className="h-8 w-8 rounded-full bg-[var(--fp-ink-wash)]" />
      </div>
      <div className="flex min-h-0 flex-1 max-md:flex-col">
        <div
          data-skeleton="column"
          className="flex shrink-0 flex-col gap-4 overflow-hidden border-r border-[var(--fp-border)] bg-[var(--fp-band)] px-3 pt-4 max-md:hidden"
          style={{ width: columnWidth }}
        >
          <ColumnRows widths={[44, 60, 92, 78, 104]} caret />
          <div className="border-t border-[var(--fp-border)] pt-3">
            <ColumnRows widths={[56, 88, 96]} />
          </div>
          <div className="border-t border-[var(--fp-border)] pt-3">
            <ColumnRows widths={[48, 76, 52, 72]} />
          </div>
        </div>
        <div className="flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden">
          <div className="flex flex-col gap-1 px-4 pb-4 pt-3.5">
            <span
              data-skeleton="heading"
              className={`${bar} my-[3px] h-[18px] w-16`}
            />
          </div>
          <div
            data-skeleton="chips"
            // Wrapping, as the chips row wraps: a row that cannot wrap sets
            // the page's minimum width past the shell's frame and the card
            // runs off the right edge.
            className="flex flex-wrap gap-2 border-b border-[var(--fp-border)] px-4 pb-3 max-md:flex-nowrap"
          >
            {/* At phone the row is one scrolling strip, so only what fits the
              first screen is drawn. */}
            {CHIP_WIDTHS.map((width, index) => (
              <span
                key={index}
                className={`h-[30px] shrink-0 rounded-[7px] border border-[var(--fp-border-strong)] bg-[var(--fp-surface)] ${
                  index >= 3 ? "max-md:hidden" : ""
                }`}
                style={{ width }}
              />
            ))}
          </div>
          <div className="flex flex-col pb-4 pt-2">
            <HomePlaceholders />
          </div>
        </div>
      </div>
      {/* The phone's bottom tab bar: Home, Documents, Records, Recent. */}
      <div className="flex h-[64px] shrink-0 items-center justify-around border-t border-[var(--fp-border)] bg-[var(--fp-surface)] md:hidden">
        {[36, 64, 52, 46].map((width, index) => (
          <span key={index} className="flex flex-col items-center gap-1.5">
            <span className="h-5 w-5 rounded-[4px] bg-[var(--fp-ink-wash)]" />
            <span className={`${bar} h-2.5`} style={{ width }} />
          </span>
        ))}
      </div>
    </div>
  );
};

export default DocumentsSkeletonLoader;
